import { Link } from "@tanstack/react-router"
import { CircleAlert, FileText } from "lucide-react"

import { cn } from "@atlant/ui"

import type { InvoiceMonthSummariesQuery } from "#gql/graphql.ts"
import { DateConverter } from "#utils/date.ts"
import { formatAmount, sortTotals } from "#utils/money.ts"

import { StatusBadge, type InvoiceStatus } from "./status-badge.tsx"

export type MonthSummary = InvoiceMonthSummariesQuery["invoiceMonthSummaries"][number]

// en-US, as formatPeriod (the month page title): en-GB abbreviates September as "Sept"
const monthFormat = new Intl.DateTimeFormat("en-US", { month: "short", timeZone: "UTC" })

// Month workflow (In review / Send / Closed) is not modelled yet
const getStatus = (_summary: MonthSummary): InvoiceStatus => "new"

interface MonthCardProps {
  slug: string
  year: number
  month: number
  aircraftId?: string
  summary?: MonthSummary
}

export const MonthCard = ({ slug, year, month, aircraftId, summary }: MonthCardProps) => {
  const label = monthFormat.format(new Date(Date.UTC(2000, month - 1, 1)))

  if (!summary) {
    return (
      <div className="bg-card flex flex-col justify-between rounded-lg border p-4 opacity-50">
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground text-lg font-semibold">{label}</span>
          <StatusBadge status="notStarted" />
        </div>
        <p className="text-muted-foreground mt-6 border-t pt-3 text-xs">
          No report imported
        </p>
      </div>
    )
  }

  const [primary, ...other] = sortTotals(summary.totals)
  const attention = [
    { label: "Late invoices", icon: FileText, count: summary.lateCount },
    { label: "Disputed invoices", icon: CircleAlert, count: summary.needsReviewCount },
  ].filter(({ count }) => count > 0)

  return (
    <Link
      to="/$slug/invoices/$period"
      params={{ slug, period: DateConverter.toPeriod(year, month) }}
      search={{ aircraftId }}
      className="bg-card hover:bg-muted/50 focus-visible:border-primary focus-visible:ring-primary/30 flex cursor-default flex-col rounded-lg border p-4 outline-none focus-visible:ring-2"
    >
      <div className="flex items-center justify-between">
        <span className="text-lg font-semibold">{label}</span>
        <StatusBadge status={getStatus(summary)} />
      </div>

      <div className="mt-3">
        {primary && (
          <p className="text-xl font-bold">
            {formatAmount(primary.amount, primary.currency)}
          </p>
        )}
        {other.map(({ currency, amount }) => (
          <p key={currency} className="text-muted-foreground text-xs">
            + {formatAmount(amount, currency)}
          </p>
        ))}
      </div>

      <p className="mt-1 mb-3 text-sm">
        {summary.invoiceCount} invoice{summary.invoiceCount === 1 ? "" : "s"}
      </p>

      {/* mt-auto: footers line up across a row when another card has extra currencies */}
      <div
        className={cn(
          "mt-auto flex flex-col gap-1 border-t pt-3 text-xs",
          attention.length === 0 ? "text-muted-foreground" : "text-foreground",
        )}
      >
        {attention.length > 0 ? (
          attention.map(({ label, icon: Icon, count }) => (
            <div key={label} className="flex items-center justify-between">
              <span className="flex items-center gap-1">
                <Icon className="size-3" />
                {label}
              </span>
              <span>{count}</span>
            </div>
          ))
        ) : (
          <span>No items requiring attention</span>
        )}
      </div>
    </Link>
  )
}
